import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import Typography from '@material-ui/core/Typography';
import PlayCircleFilledWhiteIcon from '@material-ui/icons/PlayCircleFilledWhite';
import ResponsiveUgcProxyPicture from 'client/components/ResponsiveUgcProxyPicture';
import { dp08 } from 'Mui/styles/shadow';
import { system600 } from 'Mui/styles/color';
import getUrlWrapTestCode from 'ugc/utils/getUrlWrapTestCode';
import LinkWrapper from 'ugc/utils/LinkWrapper';

import { below, breakpoints } from 'utils/breakpoint';
import { gameTypes } from 'config/promoGameTypes';
import ugcTypes from 'config/ugcTypes';

function UgcCard({
    style,
    textStyle,
    avatar,
    name,
    serialNo,
    imgUrls = [],
    title,
    isOriginRatio,
    showInfo,
    promoId,
    voteActionComponent,
    promoType,
    disableLink = false,
    isWaterFall = false,
    isMobileGroup,
    igGame = false,
    igUrl,
    isGallery = false,
    SetReady = () => {},
    length = 0,
    index = 0
}) {
    const linkUrl = `${promoId}/${serialNo}`;

    function handleGalleryClick() {
        if (!disableLink) {
            window.open(
                `/ugc/${promoId}/${serialNo}${window.location.search}${window.location.hash}`
            );
        }
    }

    const isVideoUpload = promoType === gameTypes.VIDEO_UPLOAD;
    const mediaData = imgUrls.filter(item => item.type === ugcTypes.IMAGE);
    useEffect(() => {
        if (index + 1 === length) {
            SetReady(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <LinkWrapper
            href={isGallery ? null : getUrlWrapTestCode(linkUrl)}
            style={linkStyle}
            disable={disableLink}
            isExternal={isGallery}
            onClick={isGallery ? handleGalleryClick : null}
        >
            <div
                css={[
                    containerStyle,
                    css(style),
                    !showInfo &&
                        css`
                            position: relative;
                        `
                ]}
            >
                {showInfo && (
                    <div css={infoStyle}>
                        {Boolean(avatar) && (
                            <div css={userStyle}>
                                <img
                                    css={[avatarStyle, igGame && igAvatarStyle]}
                                    src={avatar}
                                    alt={`${name}_avatar`}
                                />
                            </div>
                        )}
                        <div
                            css={css`
                                width: 100%;
                            `}
                        >
                            {Boolean(name) && (
                                <Typography css={[nameStyle, textStyle]} variant="body2">
                                    {name}
                                </Typography>
                            )}
                            {!igGame && !isGallery && (
                                <Typography css={[serialStyle, textStyle]} variant="caption">
                                    ID：{serialNo}
                                </Typography>
                            )}
                        </div>
                    </div>
                )}
                <div
                    css={[
                        imgContainerStyle({ removePadding: isOriginRatio, isVideoUpload, igGame }),
                        !showInfo &&
                            !isMobileGroup &&
                            css`
                                border-radius: 16px;
                            `
                    ]}
                >
                    {mediaData.map(({ result: imgUrl, uuid, seq }) => (
                        <ResponsiveUgcProxyPicture
                            key={uuid ?? seq}
                            src={imgUrl}
                            breakPointConfigs={{
                                normal: isWaterFall ? proxyOriginConfig : proxySquareConfig,
                                [breakpoints.xsm]: isWaterFall
                                    ? proxyOriginConfig
                                    : proxySquareConfig
                            }}
                            enableLazyLoad
                            css={[imgStyle({ removePadding: isOriginRatio })]}
                        />
                    ))}
                    {isVideoUpload && <PlayCircleFilledWhiteIcon css={iconStyle} />}
                    {igGame && (
                        <a href={igUrl}>
                            <img
                                src="https://assets.fevercdn.com/promotion/icon/ig_logo.svg"
                                alt="instagramAccount"
                                css={igIconStyle}
                            />
                        </a>
                    )}
                </div>
                {showInfo && (
                    <>
                        <div css={[titleStyle, textStyle]}>
                            <Typography variant="subtitle2" css={[textStyle]}>
                                {title}
                            </Typography>
                        </div>
                    </>
                )}
                {!isMobileGroup && voteActionComponent}
            </div>
        </LinkWrapper>
    );
}

const proxyOriginConfig = { resizeWidth: 720 };
const proxySquareConfig = { width: 720, height: 720 };

UgcCard.propTypes = {
    avatar: PropTypes.string,
    name: PropTypes.string,
    serialNo: PropTypes.number,
    imgUrls: PropTypes.array,
    title: PropTypes.string,
    showInfo: PropTypes.bool
};

export default UgcCard;

const linkStyle = css`
    text-decoration: none;
`;

const containerStyle = css`
    border-radius: 16px;
    transition: box-shadow 0.3s;
    &:hover {
        box-shadow: ${dp08.boxShadow};
    }
    ${below('sm')} {
        width: 100%;
        margin-right: 0;
        &:hover {
            box-shadow: none;
        }
    }
`;

const userStyle = css`
    align-items: center;
    display: flex;
    overflow: hidden;
    width: 20%;
`;

const nameStyle = css`
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    margin-left: 8px;
`;

const infoStyle = css`
    padding: 8px 16px;
    height: 48px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
`;

const avatarStyle = css`
    width: 32px;
    height: 32px;
    border-radius: 50%;
`;

const igAvatarStyle = css`
    width: 24px;
    height: 24px;
    border-radius: unset;
`;

const serialStyle = css`
    color: ${system600.color};
    white-space: nowrap;
    margin-left: 8px;
`;

const imgContainerStyle = ({ removePadding, isVideoUpload, igGame }) => css`
    width: 100%;
    position: relative;
    overflow: hidden;
    ${below('sm')} {
        width: 100%;
        margin-right: 0;
        ${removePadding &&
        `
        padding-top: initial;
        position: ${isVideoUpload || igGame ? 'relative' : 'static'};
        `}
    }
`;

const titleStyle = css`
    padding: 8px 16px;
    height: 48px;
    display: flex;
    align-items: center;
    & h6 {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }
`;

const imgStyle = ({ removePadding }) => css`
    width: 100%;
    display: block;
    ${below('sm')} {
        width: 100%;
        margin-right: 0;
        ${removePadding &&
        `
        top: initial;
        position: static;
        `}
    }
`;

const iconStyle = css`
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    margin: auto;
    max-width: 53px;
    max-height: 53px;
    color: #ffffff;
    width: 100%;
    height: 100%;
`;

const igIconStyle = css`
    position: absolute;
    width: 24px;
    height: 24px;
    left: 16px;
    bottom: 16px;
`;
